import { tryLoadLightDOMStyles } from '@/components/webcomponents/Global/loadDependency'
import { getTemplateNavHeaderStyles } from './NavHeader-Level1.styles'
import {
  ATTRIBUTES,
  CONTENT_WIDTH_VALUES,
  type ContentWidthValue,
} from '@/components/webcomponents/NavHeader/types/NavHeader.type'

const t = document.createElement('template')
t.innerHTML = `
  <header class="ascend-header" role="banner">
    <!-- Top Header: -->
    <nav class="main-nav" role="navigation" aria-label="Main Navigation">
      <!-- logo -->
      <slot name="logo"></slot>
      <!-- Site action & profile menu -->
      <slot name="utilityNavigation"></slot>
    </nav>

    <div class="tab-header">
      <slot name="primarySearchContainer"></slot>
      <slot name="primaryNavigation"></slot>
    </div>
    
  </header>
`
const TemplateNavHeader = t

class NavHeader extends HTMLElement {
  private isOpen = false
  private isUpdating = false

  static get observedAttributes() {
    return [ATTRIBUTES.HEADER_CONTENT_WIDTH, ATTRIBUTES.HIDE_LOGO]
  }

  constructor() {
    super()

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.appendChild(getTemplateNavHeaderStyles().content.cloneNode(true))
    shadow.appendChild(TemplateNavHeader.content.cloneNode(true))

    tryLoadLightDOMStyles('NavHeader-Level1.css')
  }

  connectedCallback() {
    document.AscendAwesome?.LogManager?.logDebug('NavigationHeaderLevel1 connectedCallback: ')

    this.addEventListener('toggle-hamburger', this.handleHamburgerToggle as EventListener)
    this.addEventListener('search-state-changed', this.handleSearchStateChange as EventListener)
    this.addEventListener('search-icon-clicked', this.handleSearchIconClick as EventListener)

    // Initialize header-content-width attribute
    this.updateContentWidthClass()

    if (document.querySelector('ts-nav-enterprise')) {
      this.setAttribute(ATTRIBUTES.HIDE_LOGO, '')
    }
  }
  disconnectedCallback() {
    this.removeEventListener('toggle-hamburger', this.handleHamburgerToggle as EventListener)
    this.removeEventListener('search-state-changed', this.handleSearchStateChange as EventListener)
    this.removeEventListener('search-icon-clicked', this.handleSearchIconClick as EventListener)
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (name === ATTRIBUTES.HEADER_CONTENT_WIDTH && oldValue !== newValue) {
      this.updateContentWidthClass()
    }
  }

  private handleSearchStateChange = (event: CustomEvent) => {
    const { visible } = event.detail
    const value = visible.toString()

    // Desktop / mobile search containers
    this.querySelectorAll('ts-navheader-search-container').forEach((el) =>
      el.setAttribute('search-visible', value),
    )

    // Utility search icon (button will auto-sync from this)
    this.querySelectorAll('ts-navheader-utility-search').forEach((el) =>
      el.setAttribute('search-visible', value),
    )

    if (visible) {
      this.updateHeaderState(true)
    }
  }

  private handleSearchIconClick = (event: CustomEvent) => {
    console.log('Search icon clicked:', event.detail)
  }

  private handleHamburgerToggle = () => {
    this.updateHeaderState(!this.isOpen)
  }

  private updateHeaderState(isOpen: boolean) {
    this.isOpen = isOpen
    this.classList.toggle('mobile-nav-open', this.isOpen)
    this.querySelectorAll('ts-navheader-utility-hamburger').forEach((el) => {
      if (this.isOpen) {
        el.setAttribute('menu-open', String(isOpen))
      } else {
        el.removeAttribute('menu-open')
      }
    })
  }

  private updateContentWidthClass() {
    // Prevent infinite loop
    if (this.isUpdating) return
    this.isUpdating = true

    try {
      const contentWidth =
        this.getAttribute(ATTRIBUTES.HEADER_CONTENT_WIDTH) || CONTENT_WIDTH_VALUES.CONTAINED
      const validValues = Object.values(CONTENT_WIDTH_VALUES)
      const finalValue = validValues.includes(contentWidth as ContentWidthValue)
        ? contentWidth
        : CONTENT_WIDTH_VALUES.CONTAINED

      // Log warning if invalid value provided
      if (contentWidth !== finalValue) {
        document.AscendAwesome?.LogManager?.logWarning(
          `NavHeader-Level1: Invalid header-content-width value "${contentWidth}". Using default "${CONTENT_WIDTH_VALUES.CONTAINED}". Valid values: ${validValues.join(', ')}`,
        )
      }

      // Normalize to valid value if needed
      if (contentWidth !== finalValue) {
        this.setAttribute(ATTRIBUTES.HEADER_CONTENT_WIDTH, finalValue)
      }
    } finally {
      this.isUpdating = false
    }
  }
}
if (!customElements.get('ts-navheader-level1')) {
  customElements.define('ts-navheader-level1', NavHeader)
}
// ...existing code...
